import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { Layer, Stage } from 'react-konva';
import Konva from 'konva';
import classNames from 'classnames';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Increase from '@material-ui/icons/Add';
import Decrease from '@material-ui/icons/Remove';
import FilterCenterFocusIcon from '@material-ui/icons/FilterCenterFocus';
import { withStyles, WithStyles } from '@material-ui/core/styles';

import TableElement from './TableElement';
import ImageElement from './ImageElement';
import RectElement from './RectElement';
import EllipseElement from './EllipseElement';
import TextElement from './TextElement';

import {
  genAvailableTableMap,
  getScaleToFit,
  handleTouchMove,
  handleZoomChange,
  hasCapacity,
  SCALE_MAX,
  SCALE_MIN,
  scaleDown,
  scaleUp,
} from './utils';

import { TActiveTables } from '../../../../types/IReserveModalStore';
import { ITable } from '../../../../types/IRestaurantTablesStore';
import {
  TElementAdaptScheme,
  TSectionAdaptSchema,
  TSectionElementScheme,
  TSectionImageScheme,
  TSectionMarkScheme,
  TSectionTableScheme,
} from '../../../../types/IRestaurantTablesSchemaStore';

import styles from './styles';

// to avoid issue with multi-touch scaling
Konva.hitOnDragEnabled = true;

const StyledButton = withStyles({
  root: {
    minWidth: 38,
    maxWidth: 38,
  },
})(Button);

interface IProps extends WithStyles<typeof styles> {
  handleSelect?: (table: ITable) => void;
  tables: ITable[],
  activeTables: TActiveTables;
  data: TSectionAdaptSchema;
  customWidthOffset?: boolean;
  persons: number;
  readOnly?: boolean;
}

function RestaurantSchema(props: IProps) {
  const {
    classes, data, tables, activeTables, handleSelect, customWidthOffset, persons,
    readOnly,
  } = props;

  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [scale, setScale] = useState(0.5);
  const [offsetWidth, setOffsetWidth] = useState(250);

  const availableTables = useMemo(() => genAvailableTableMap(tables), [tables]);

  const divEl = useRef<HTMLDivElement>(null);
  const shapeRef = useRef<Konva.Stage>(null);

  const scaleTo = useCallback((scale: number) => {
    setScale(scale);
    if (shapeRef.current) {
      const stage = shapeRef.current;
      // reset position after dragging/scaling
      stage.setPosition({ x: 0, y: 0 });
      stage.batchDraw();
    }
  }, [shapeRef, setScale]);

  const scaleToFit = useCallback(() => {
    if (data && width > 0 && height > 0) {
      const newScale = getScaleToFit(data, width, height);
      scaleTo(newScale);
    }
  }, [data, width, height, scaleTo]);

  const onTableSelect = useCallback((data: ITable) => {
    const table = tables.find(t => t.id === data.id);
    if (table && typeof handleSelect === 'function') handleSelect(table);
  }, [tables, handleSelect]);

  // Вычисляем размеры контейнера и инициализируем канвас
  useLayoutEffect(() => {
    let rafId: number | null = null;

    const resize = () => {
      // Determine rectangle on screen
      if (divEl.current) {
        // Используем offsetWidth и offsetHeight для более точного измерения
        const containerWidth = divEl.current.offsetWidth || divEl.current.clientWidth;
        const containerHeight = divEl.current.offsetHeight || divEl.current.clientHeight;

        // Устанавливаем минимальные размеры, если контейнер еще не отрендерился
        if (containerWidth > 0 && containerHeight > 0) {
          setWidth(containerWidth);
          setHeight(containerHeight);
        } else {
          // Если размеры еще не определены, используем размеры окна как fallback
          const fallbackWidth = window.innerWidth * 0.6;
          const fallbackHeight = window.innerHeight * 0.6;
          if (fallbackWidth > 0 && fallbackHeight > 0) {
            setWidth(fallbackWidth);
            setHeight(fallbackHeight);
          }
        }

        const offsetLeft = divEl.current.offsetLeft;
        // Используем offsetLeft только если он положительный и разумный
        if (offsetLeft > 0 && offsetLeft < window.innerWidth) {
          setOffsetWidth(offsetLeft);
        } else {
          setOffsetWidth(0);
        }
      }
    };

    const debouncedResize = () => {
      // Отменяем предыдущий запрос, если он есть
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      // Используем requestAnimationFrame для отложенного выполнения
      rafId = requestAnimationFrame(() => {
        resize();
        rafId = null;
      });
    };

    // Вызываем resize сразу
    resize();

    // Используем ResizeObserver для более точного отслеживания изменений размера
    // Добавляем debounce через requestAnimationFrame, чтобы избежать бесконечного цикла
    const resizeObserver = new ResizeObserver(() => {
      debouncedResize();
    });

    if (divEl.current) {
      resizeObserver.observe(divEl.current);
    }

    const handleWindowResize = () => {
      debouncedResize();
    };

    window.addEventListener('resize', handleWindowResize);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [data]);


  // handle zoom change
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const stage = shapeRef.current;
    if (stage) {
      return handleZoomChange(stage, setScale);
    }
  }, [shapeRef.current]);

  // handle touch move
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const stage = shapeRef.current;
    if (stage) {
      return handleTouchMove(stage, setScale);
    }
  }, [shapeRef.current]);

  // handle data change - пересчитываем масштаб при изменении данных или размеров
  useEffect(() => {
    if (data && width > 0 && height > 0) {
      scaleToFit();
    }
  }, [data, width, height, scaleToFit]);


  // Для мобильных устройств или когда offsetWidth некорректный, используем 100% ширины
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;
  const shouldUseCustomWidth = customWidthOffset && offsetWidth && offsetWidth > 0 && !isMobile;

  return (
    <div
      ref={divEl}
      className={classes.wrap}
      style={{
        width: shouldUseCustomWidth ? `calc(100vw - ${offsetWidth}px - 24px)` : '100%',
      }}
    >
      <div className={classes.buttonRight}>
        <StyledButton
          className={classNames(classes.button, classes.buttonShadow)}
          onClick={scaleToFit}
        >
          <FilterCenterFocusIcon />
        </StyledButton>
        <ButtonGroup
          className={classNames(classes.buttonGroup, classes.buttonShadow)}
          orientation="vertical"
          aria-label="vertical button group"
        >
          <StyledButton
            className={classes.button}
            onClick={() => scaleTo(scaleUp(scale))}
            disabled={scale >= SCALE_MAX}
          >
            <Increase />
          </StyledButton>
          <StyledButton
            className={classes.button}
            onClick={() => scaleTo(scaleDown(scale))}
            disabled={scale <= SCALE_MIN}
          >
            <Decrease />
          </StyledButton>
        </ButtonGroup>
      </div>
      {Boolean(width) && Boolean(height) && Boolean(data?.elements?.length) && (
        <Stage
          ref={shapeRef}
          width={width}
          height={height}
          scaleX={scale}
          scaleY={scale}
          draggable
        >
          <Layer>
            {data.elements.map((item: TElementAdaptScheme, i: number) => {
              const { type, element } = item;
              switch (type) {
                case 'table': {
                  const data: TSectionTableScheme = element as TSectionTableScheme;
                  const { table }: { table: ITable } = data;
                  // checking for enough capacity behind a table
                  const isCapable = hasCapacity(table, persons);
                  const selected = !!activeTables[table.id];
                  return (
                    <TableElement
                      key={table.id}
                      data={data}
                      handleSelect={onTableSelect}
                      active={selected}
                      disabled={!availableTables[table.id] || !isCapable || readOnly}
                    />
                  );
                }
                case 'rect': {
                  const data: TSectionElementScheme = element as TSectionElementScheme;
                  return (
                    <RectElement
                      key={i.toString()}
                      data={data}
                    />
                  );
                }
                case 'ellipse': {
                  const data: TSectionElementScheme = element as TSectionElementScheme;
                  return (
                    <EllipseElement
                      key={i.toString()}
                      data={data}
                    />
                  );
                }
                case 'image': {
                  const data: TSectionImageScheme = element as TSectionImageScheme;
                  return (
                    <ImageElement
                      key={data.image ? data.image.id.toString() : i.toString()}
                      data={data}
                    />
                  );
                }
                case 'text': {
                  const data: TSectionMarkScheme = element as TSectionMarkScheme;
                  return <TextElement key={i.toString()} data={data} />;
                }
                default: {
                  return null;
                }
              }
            })}
          </Layer>
        </Stage>
      )}
    </div>
  );
}

export default withStyles(styles)(RestaurantSchema);
